import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Define interfaces for ResourceTag and API responses
export interface ResourceTag {
  id: string;
  resourceType: string;
  resourceId: string;
  tagDefinitionId: string;
  tagDefinitionKey: string;
  tagValueId: string | null;
  tagValueValue: string | null;
  tagValueDisplayName: string | null;
  freeformValue: string | null;
  taggedByUserId: string;
  taggedAt: string;
}

interface ResourceTagListResponse {
  resourceTags: ResourceTag[];
}

export interface AddResourceTagRequest {
  tagDefinitionId: string;
  tagValueId?: string | null;
  freeformValue?: string | null;
}

export interface BulkAddResourceTagsRequest {
  resourceIds: string[];
  tags: AddResourceTagRequest[];
}

interface ResourceTagResponse {
  id: string;
  resourceType: string;
  resourceId: string;
  tagDefinitionId: string;
  tagDefinitionKey: string;
  tagValueId: string | null;
  tagValueValue: string | null;
  tagValueDisplayName: string | null;
  freeformValue: string | null;
  taggedByUserId: string;
  taggedAt: string;
}

export interface ApiErrorResponse {
  error: string;
  description?: string;
}

const backendApi = config.backendApi;

// Fetch tags for a specific resource
const fetchResourceTags = async (resourceType: string, resourceId: string): Promise<ResourceTag[]> => {
  const response = await axios.get<ResourceTagListResponse>(
    `${backendApi}/api/resource-tags/${resourceType}/${resourceId}`,
    { withCredentials: true }
  );
  return response.data.resourceTags;
};

// Add a tag to a resource
const addTagToResource = async (
  resourceType: string,
  resourceId: string,
  data: AddResourceTagRequest
): Promise<ResourceTagResponse> => {
  const response = await axios.post<ResourceTagResponse>(
    `${backendApi}/api/resource-tags/${resourceType}/${resourceId}`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

// Bulk add tags to multiple resources
const bulkAddTags = async (
  resourceType: string,
  data: BulkAddResourceTagsRequest
): Promise<ResourceTagListResponse> => {
  const response = await axios.post<ResourceTagListResponse>(
    `${backendApi}/api/resource-tags/bulk/${resourceType}`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

// Delete a specific resource tag by ID
const deleteResourceTag = async (id: string): Promise<void> => {
  await axios.delete(`${backendApi}/api/resource-tags/${id}`, { withCredentials: true });
};

// Remove all instances of a tag definition from a resource
const removeTagFromResource = async (
  resourceType: string,
  resourceId: string,
  tagDefinitionId: string
): Promise<void> => {
  await axios.delete(
    `${backendApi}/api/resource-tags/${resourceType}/${resourceId}/${tagDefinitionId}`,
    { withCredentials: true }
  );
};

// Hook to fetch tags for a specific resource
export function useResourceTags(
  organizationId: string | null | undefined,
  resourceType: string | null | undefined,
  resourceId: string | null | undefined
) {
  const contextReady = useActiveContextReady();

  return useQuery<ResourceTag[], AxiosError<ApiErrorResponse>>({
    queryKey: ['resourceTags', organizationId, resourceType, resourceId],
    queryFn: () => {
      if (!organizationId || !resourceType || !resourceId) {
        throw new Error("organizationId, resourceType, and resourceId are required to fetch resource tags.");
      }
      return fetchResourceTags(resourceType, resourceId);
    },
    enabled: !!organizationId && !!resourceType && !!resourceId && contextReady,
  });
}

// Hook to add a tag to a resource
export function useAddTagToResource() {
  const queryClient = useQueryClient();

  return useMutation<
    ResourceTagResponse,
    AxiosError<ApiErrorResponse>,
    { resourceType: string; resourceId: string; data: AddResourceTagRequest }
  >({
    mutationFn: ({ resourceType, resourceId, data }) => addTagToResource(resourceType, resourceId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['resourceTags'] });
    },
  });
}

// Hook to bulk add tags to multiple resources
export function useBulkAddTags() {
  const queryClient = useQueryClient();

  return useMutation<
    ResourceTagListResponse,
    AxiosError<ApiErrorResponse>,
    { resourceType: string; data: BulkAddResourceTagsRequest }
  >({
    mutationFn: ({ resourceType, data }) => bulkAddTags(resourceType, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['resourceTags'] });
    },
  });
}

// Hook to delete a specific resource tag by ID
export function useDeleteResourceTag() {
  const queryClient = useQueryClient();

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  return useMutation<void, AxiosError<ApiErrorResponse>, string>({
    mutationFn: deleteResourceTag,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['resourceTags'] });
    },
  });
}

// Hook to remove all instances of a tag definition from a resource
export function useRemoveTagFromResource() {
  const queryClient = useQueryClient();

  /* eslint-disable @typescript-eslint/no-invalid-void-type */
  return useMutation<
    void,
    AxiosError<ApiErrorResponse>,
    { resourceType: string; resourceId: string; tagDefinitionId: string }
  >({
    mutationFn: ({ resourceType, resourceId, tagDefinitionId }) =>
      removeTagFromResource(resourceType, resourceId, tagDefinitionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['resourceTags'] });
    },
  });
  /* eslint-enable @typescript-eslint/no-invalid-void-type */
}
